import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, Star, Eye, Copy, TrendingUp, Users, Clock, ArrowRight } from 'lucide-react';

const FunnelTemplateLibrary = ({ categories, onTemplateSelect, selectedTemplate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('popular');

  const templates = [
    {
      id: 1,
      title: 'Online Course Launch Funnel',
      category: 'online-courses',
      description: 'Complete funnel for launching and selling online courses with pre-launch sequence',
      conversionRate: 32,
      users: 1247,
      difficulty: 'intermediate',
      estimatedTime: '2-3 hours',
      features: ['Email sequences', 'Landing pages', 'Payment integration', 'Student onboarding'],
      preview: '/api/placeholder/300/200',
      rating: 4.8,
      reviews: 156,
      isPopular: true
    },
    {
      id: 2,
      title: 'Coaching Consultation Funnel',
      category: 'coaching',
      description: 'Automated funnel to book high-value coaching consultations',
      conversionRate: 28,
      users: 892,
      difficulty: 'beginner',
      estimatedTime: '1-2 hours',
      features: ['Booking calendar', 'Qualification forms', 'Follow-up sequences', 'Payment processing'],
      preview: '/api/placeholder/300/200',
      rating: 4.6,
      reviews: 89,
      isPopular: false
    },
    {
      id: 3,
      title: 'Podcast Growth Funnel',
      category: 'podcasts',
      description: 'Comprehensive funnel to grow podcast audience and monetize listeners',
      conversionRate: 24,
      users: 634,
      difficulty: 'intermediate',
      estimatedTime: '3-4 hours',
      features: ['Subscriber magnets', 'Episode promotion', 'Sponsor integration', 'Community building'],
      preview: '/api/placeholder/300/200',
      rating: 4.7,
      reviews: 73,
      isPopular: true
    },
    {
      id: 4,
      title: 'Newsletter Monetization Funnel',
      category: 'newsletters',
      description: 'Transform newsletter subscribers into paying customers',
      conversionRate: 35,
      users: 1456,
      difficulty: 'beginner',
      estimatedTime: '1-2 hours',
      features: ['Subscription forms', 'Content upgrades', 'Segmentation', 'Product promotions'],
      preview: '/api/placeholder/300/200',
      rating: 4.9,
      reviews: 203,
      isPopular: true
    },
    {
      id: 5,
      title: 'Digital Product Sales Funnel',
      category: 'digital-downloads',
      description: 'High-converting funnel for digital product sales and delivery',
      conversionRate: 29,
      users: 987,
      difficulty: 'intermediate',
      estimatedTime: '2-3 hours',
      features: ['Product showcases', 'Instant delivery', 'Upsells', 'Customer support'],
      preview: '/api/placeholder/300/200',
      rating: 4.5,
      reviews: 124,
      isPopular: false
    },
    {
      id: 6,
      title: 'Lead Magnet Landing Page',
      category: 'landing-pages',
      description: 'Optimized landing page funnel for lead generation',
      conversionRate: 42,
      users: 2134,
      difficulty: 'beginner',
      estimatedTime: '30-60 minutes',
      features: ['A/B testing', 'Form optimization', 'Thank you pages', 'Email integration'],
      preview: '/api/placeholder/300/200',
      rating: 4.8,
      reviews: 312,
      isPopular: true
    }
  ];

  const filteredTemplates = templates?.filter(template => {
    const matchesSearch = template?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         template?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesCategory = filterCategory === 'all' || template?.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const sortedTemplates = [...filteredTemplates]?.sort((a, b) => {
    switch (sortBy) {
      case 'popular':
        return b?.users - a?.users;
      case 'conversion':
        return b?.conversionRate - a?.conversionRate;
      case 'rating':
        return b?.rating - a?.rating;
      case 'newest':
        return b?.id - a?.id;
      default:
        return 0;
    }
  });

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800';
      case 'advanced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Funnel Template Library</h2>
        <p className="text-gray-600">Choose from proven, high-converting funnel templates</p>
      </div>
      {/* Filters and Search */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e?.target?.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white min-w-48"
            >
              <option value="all">All Categories</option>
              {categories?.map((category) => (
                <option key={category?.id} value={category?.id}>
                  {category?.title}
                </option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
          >
            <option value="popular">Most Popular</option>
            <option value="conversion">Highest Conversion</option>
            <option value="rating">Highest Rated</option>
            <option value="newest">Newest</option>
          </select>
        </div>
      </div>
      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedTemplates?.map((template) => (
          <motion.div
            key={template?.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -4 }}
            className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group"
          >
            {/* Template Preview */}
            <div className="relative h-48 bg-gradient-to-r from-purple-100 to-blue-100 overflow-hidden">
              <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <button className="bg-white text-purple-600 px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                  <Eye className="w-4 h-4" />
                  <span>Preview</span>
                </button>
              </div>
              {template?.isPopular && (
                <div className="absolute top-3 left-3 bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                  <TrendingUp className="w-3 h-3" />
                  <span>Popular</span>
                </div>
              )}
              <div className="absolute top-3 right-3 bg-white rounded-full p-2 shadow-sm">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(template?.difficulty)}`}>
                  {template?.difficulty}
                </span>
              </div>
            </div>

            {/* Template Info */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                  {template?.title}
                </h3>
                <div className="flex items-center space-x-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" />
                  <span className="text-sm text-gray-600">{template?.rating}</span>
                </div>
              </div>
              
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {template?.description}
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-xl font-bold text-purple-600">{template?.conversionRate}%</div>
                  <div className="text-xs text-gray-500">Conversion Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-blue-600">{template?.users?.toLocaleString()}</div>
                  <div className="text-xs text-gray-500">Users</div>
                </div>
              </div>

              {/* Features */}
              <div className="mb-4">
                <div className="flex flex-wrap gap-1">
                  {template?.features?.slice(0, 3)?.map((feature, index) => (
                    <span 
                      key={index}
                      className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs"
                    >
                      {feature}
                    </span>
                  ))}
                  {template?.features?.length > 3 && (
                    <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">
                      +{template?.features?.length - 3} more
                    </span>
                  )}
                </div>
              </div>

              {/* Time Estimate */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-1 text-sm text-gray-500">
                  <Clock className="w-4 h-4" />
                  <span>{template?.estimatedTime}</span>
                </div>
                <div className="flex items-center space-x-1 text-sm text-gray-500">
                  <Users className="w-4 h-4" />
                  <span>{template?.reviews} reviews</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <button
                  onClick={() => onTemplateSelect(template)}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                >
                  <span>Use Template</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                  <Copy className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      {/* No Results */}
      {sortedTemplates?.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Search className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
          <p className="text-gray-600">Try adjusting your search or filter criteria</p>
        </div>
      )}
    </div>
  );
};

export default FunnelTemplateLibrary;